import { z } from 'zod';
import {
  CHANNEL_ROLE_IDS,
  CLUSTER_IDS,
  CLUSTER_LEVEL_IDS,
  CONFIDENCE_IDS,
  CONTEXT_FIELD_IDS,
  OVERALL_BAND_IDS,
  QUESTION_IDS,
  RECOMMENDATION_GROUPS,
  SEVERITY_IDS,
  VISIBILITY_MODEL_IDS,
} from '../types/ids';

/** Zod-Schemas zur Runtime-Validierung des Datenmodells (DAT-002) und als Build-Grundlage (DAT-001). */

export const scoreValueSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);

export const questionIdSchema = z.enum(QUESTION_IDS);
export const clusterIdSchema = z.enum(CLUSTER_IDS);
export const contextFieldIdSchema = z.enum(CONTEXT_FIELD_IDS);

// --- 00-manifest.json ---
export const manifestSchema = z.object({
  package: z.string(),
  schemaVersion: z.string(),
  scoringVersion: z.string(),
  contentVersion: z.string(),
  language: z.string(),
  createdFor: z.string(),
  principles: z.array(z.string()),
  files: z.array(z.object({ path: z.string(), purpose: z.string() })),
});
export type Manifest = z.infer<typeof manifestSchema>;

// --- 01-app-config.json ---
export const appConfigSchema = z.object({
  schemaVersion: z.string(),
  scoringVersion: z.string(),
  contentVersion: z.string(),
  test: z.object({
    questionCount: z.number(),
    scorePerQuestion: z.object({ minimum: z.number(), maximum: z.number() }),
    maximumOverallScore: z.number(),
    showScoresDuringTest: z.boolean(),
    allowBackNavigation: z.boolean(),
    allowAnswerChanges: z.boolean(),
    requireAllQuestions: z.boolean(),
    showReviewBeforeResult: z.boolean(),
  }),
  shuffle: z.object({
    enabled: z.boolean(),
    scope: z.string(),
    method: z.string(),
    generateOncePerSession: z.boolean(),
    persistOrderPerQuestion: z.boolean(),
    reshuffleWhenNavigatingBack: z.boolean(),
    reshuffleAfterPageReload: z.boolean(),
    reshuffleOnNewTest: z.boolean(),
    seedField: z.string(),
    storedOrderField: z.string(),
  }),
  clusterScoring: z.object({
    method: z.string(),
    weightsMustSumTo: z.number(),
    weightTolerance: z.number(),
    formula: z.object({ rawScore: z.string(), maximum: z.string(), percentage: z.string() }),
    affectsOverallScore: z.boolean(),
    displayRawDecimals: z.boolean(),
    displayPercentage: z.boolean(),
    displayContributionDetails: z.boolean(),
  }),
  tensions: z.object({
    enabled: z.boolean(),
    deduplicateByRuleId: z.boolean(),
    sortBy: z.array(z.string()),
    maximumFeaturedCards: z.number(),
    showAllInDetailSection: z.boolean(),
    severityOrder: z.array(z.enum(SEVERITY_IDS)),
    scorePenalty: z.number(),
    mayAdjustRecommendedChannelRole: z.boolean(),
    note: z.string(),
  }),
  recommendations: z.object({
    groupBy: z.array(z.enum(RECOMMENDATION_GROUPS)),
    maximumPerGroup: z.number(),
    deduplicate: z.boolean(),
    priorityOrder: z.array(z.string()),
  }),
  language: z.object({
    locale: z.string(),
    tone: z.string(),
    avoidTerms: z.array(z.string()),
    preferredTerms: z.record(z.string(), z.string()),
  }),
  privacy: z.object({
    processing: z.string(),
    backendRequired: z.boolean(),
    accountRequired: z.boolean(),
    defaultPersistence: z.string(),
    allowDeleteLocalData: z.boolean(),
    suggestedNotice: z.string(),
  }),
  resultLayout: z.array(z.string()),
});
export type AppConfig = z.infer<typeof appConfigSchema>;

// --- 02-clusters.json ---
export const clusterSchema = z.object({
  id: clusterIdSchema,
  order: z.number(),
  title: z.string(),
  shortTitle: z.string(),
  description: z.string(),
  questionsAnswered: z.array(z.string()),
  relevanceText: z.string(),
  icon: z.string(),
});
export const clustersSchema = z.array(clusterSchema).length(6);
export type Cluster = z.infer<typeof clusterSchema>;

// --- 03-questions.json ---
export const questionOptionSchema = z
  .object({
    id: z.string(),
    score: scoreValueSchema,
    label: z.string(),
    tags: z.array(z.string()).optional(),
  })
  .loose();

export const questionExampleSchema = z.object({
  heading: z.string(),
  items: z.array(z.string()),
});

export const questionSchema = z.object({
  id: questionIdSchema,
  number: z.number().min(1).max(14),
  title: z.string(),
  question: z.string(),
  shortHint: z.string(),
  explanation: z.string(),
  cautions: z.array(z.string()).default([]),
  examples: z.array(questionExampleSchema).default([]),
  glossaryTerms: z.array(z.string()).default([]),
  options: z.array(questionOptionSchema).length(3),
  relatedTensionRuleIds: z.array(z.string()).default([]),
});
export const questionsSchema = z.array(questionSchema).length(14);
export type Question = z.infer<typeof questionSchema>;
export type QuestionOption = z.infer<typeof questionOptionSchema>;

// --- 04-cluster-matrix.json ---
export const clusterWeightsSchema = z.object({
  audience_demand: z.number(),
  topic_content: z.number(),
  trust_visibility: z.number(),
  daily_execution: z.number(),
  sales_readiness: z.number(),
  instagram_independence: z.number(),
});
export const clusterMatrixSchema = z.object({
  schemaVersion: z.string(),
  rule: z.string(),
  clusters: z.array(clusterIdSchema).length(6),
  rows: z.record(questionIdSchema, clusterWeightsSchema),
});
export type ClusterMatrix = z.infer<typeof clusterMatrixSchema>;
export type ClusterWeights = z.infer<typeof clusterWeightsSchema>;

// --- 05-context-questions.json ---
export const contextOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});
export const contextQuestionSchema = z.object({
  id: contextFieldIdSchema,
  title: z.string(),
  question: z.string(),
  required: z.boolean(),
  scored: z.boolean(),
  options: z.array(contextOptionSchema).min(1),
});
export const contextQuestionsSchema = z.array(contextQuestionSchema).length(9);
export type ContextQuestion = z.infer<typeof contextQuestionSchema>;

// --- 06-result-bands.json ---
export const overallBandSchema = z.object({
  id: z.enum(OVERALL_BAND_IDS),
  minimumScore: z.number(),
  maximumScore: z.number(),
  minimumPercentage: z.number(),
  maximumPercentage: z.number(),
  label: z.string(),
  shortLabel: z.string(),
  summary: z.string(),
  defaultRole: z.enum(CHANNEL_ROLE_IDS),
});
export const clusterBandSchema = z.object({
  id: z.enum(CLUSTER_LEVEL_IDS),
  minimumPercentage: z.number(),
  maximumPercentage: z.number(),
  label: z.string(),
});
export const resultBandsSchema = z.object({
  overall: z.array(overallBandSchema).length(4),
  cluster: z.array(clusterBandSchema).length(5),
});
export type OverallBandDefinition = z.infer<typeof overallBandSchema>;
export type ClusterBandDefinition = z.infer<typeof clusterBandSchema>;

// --- 07-tension-rules.json ---
const baseConditionSchema = z.object({
  type: z.literal('question_score'),
  questionId: questionIdSchema,
  operator: z.enum(['eq', 'neq', 'gte', 'lte', 'in']),
  value: z.union([scoreValueSchema, z.array(scoreValueSchema)]),
});
const contextConditionSchema = z.object({
  type: z.literal('context_value'),
  field: contextFieldIdSchema,
  operator: z.enum(['eq', 'neq', 'in']),
  value: z.union([z.string(), z.array(z.string())]),
});
const overallPercentageConditionSchema = z.object({
  type: z.literal('overall_percentage'),
  operator: z.enum(['gte', 'lte']),
  value: z.number(),
});
const clusterPercentageConditionSchema = z.object({
  type: z.literal('cluster_percentage'),
  clusterId: clusterIdSchema,
  operator: z.enum(['gte', 'lte']),
  value: z.number(),
});
export type RuleCondition =
  | z.infer<typeof baseConditionSchema>
  | z.infer<typeof contextConditionSchema>
  | z.infer<typeof overallPercentageConditionSchema>
  | z.infer<typeof clusterPercentageConditionSchema>
  | { type: 'compound_any'; conditions: RuleCondition[] };

export const ruleConditionSchema: z.ZodType<RuleCondition> = z.lazy(() =>
  z.union([
    baseConditionSchema,
    contextConditionSchema,
    overallPercentageConditionSchema,
    clusterPercentageConditionSchema,
    z.object({
      type: z.literal('compound_any'),
      conditions: z.array(ruleConditionSchema),
    }),
  ]),
);

export const tensionRuleSchema = z.object({
  id: z.string(),
  title: z.string(),
  shortLabel: z.string(),
  severity: z.enum(SEVERITY_IDS),
  priority: z.number(),
  conditionMode: z.enum(['all', 'any']),
  conditions: z.array(ruleConditionSchema),
  summary: z.string(),
  explanation: z.string(),
  recommendationIds: z.array(z.string()),
  sourceQuestionIds: z.array(questionIdSchema).default([]),
  sourceContextFields: z.array(contextFieldIdSchema).default([]),
  businessImpact: z.string().optional(),
  personalImpact: z.string().optional(),
});
export const tensionRulesSchema = z.array(tensionRuleSchema).length(15);
export type TensionRule = z.infer<typeof tensionRuleSchema>;

// --- 08-recommendation-rules.json ---
export const recommendationSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.string(),
  defaultPriority: z.enum(RECOMMENDATION_GROUPS),
  description: z.string(),
  actionItems: z.array(z.string()),
});
export type Recommendation = z.infer<typeof recommendationSchema>;

export const channelRoleRuleSchema = z.object({
  id: z.string(),
  priority: z.number(),
  conditions: z.array(ruleConditionSchema),
  result: z.object({
    role: z.enum(CHANNEL_ROLE_IDS),
    label: z.string(),
    confidence: z.enum(CONFIDENCE_IDS),
  }),
});
export type ChannelRoleRule = z.infer<typeof channelRoleRuleSchema>;

export const visibilityModelRuleSchema = z.object({
  id: z.string(),
  priority: z.number(),
  conditions: z.array(ruleConditionSchema),
  result: z.object({
    model: z.enum(VISIBILITY_MODEL_IDS),
    label: z.string(),
  }),
});
export type VisibilityModelRule = z.infer<typeof visibilityModelRuleSchema>;

export const rankingRulesSchema = z.object({
  strengths: z.object({
    minimumQuestionScore: scoreValueSchema,
    minimumClusterPercentage: z.number(),
    maximumItems: z.number(),
  }),
  weakPoints: z.object({
    maximumQuestionScore: scoreValueSchema,
    maximumClusterPercentage: z.number(),
    maximumItems: z.number(),
    businessCriticalQuestionIds: z.array(questionIdSchema),
    businessCriticalPriorityBonus: z.number(),
  }),
});
export type RankingRules = z.infer<typeof rankingRulesSchema>;

export const recommendationRulesFileSchema = z.object({
  recommendations: z.array(recommendationSchema).length(36),
  channelRoleRules: z.array(channelRoleRuleSchema),
  visibilityModelRules: z.array(visibilityModelRuleSchema),
  rankingRules: rankingRulesSchema,
});
export type RecommendationRulesFile = z.infer<typeof recommendationRulesFileSchema>;

// --- 09-glossary.json ---
export const glossaryTermSchema = z.object({
  id: z.string(),
  term: z.string(),
  simpleDefinition: z.string(),
  avoid: z.string().optional(),
  note: z.string().optional(),
});
export const glossarySchema = z.array(glossaryTermSchema);
export type GlossaryTerm = z.infer<typeof glossaryTermSchema>;
